import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, ExternalLink, Mail, Phone, Plus } from "lucide-react";
import { toast } from "sonner";
import adminApi, { apiError } from "@/lib/adminApi";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import {
  Avatar, Btn, Drawer, EmptyState, JourneyStepper, KvRow, Panel, RouteLine,
  Skeleton, StatusBadge, T, humanActivity, money, timeAgo, COMM_LABELS, DeleteButton,
} from "@/components/admin/ui";

const INCIDENT_TYPES = ["retard", "no-show", "client absent", "chauffeur absent", "véhicule changé", "plainte", "objet oublié", "remboursement", "paiement"];

function RefundDrawer({ booking, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (full) => {
    if (busy) return;
    setBusy(true);
    try {
      await adminApi.post(`/bookings/${booking.id}/refund`, full ? {} : { amount_chf: Number(amount) });
      toast.success(full ? "Remboursement total effectué" : "Remboursement partiel effectué");
      onDone();
    } catch (error) {
      toast.error("Remboursement impossible", { description: apiError(error, "Réessayez.") });
    } finally { setBusy(false); }
  };
  return (
    <Drawer title={`Rembourser ${booking.tracking_code}`} onClose={onClose} testId="refund-drawer">
      <div className={T.card + " p-5"}>
        <p className="text-sm text-slate">Montant payé</p>
        <p className="mt-1 font-mono text-2xl font-semibold text-ink">{money(booking.price_chf)}</p>
        <label className="mt-5 block text-[13px] font-medium text-ink" htmlFor="refund-amount">Montant partiel (CHF)</label>
        <input id="refund-amount" type="number" step="0.05" min="0" max={booking.price_chf} value={amount}
          onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount" className={`${T.input} mt-1.5 font-mono`} />
        <div className="mt-5 space-y-2">
          <Btn className="w-full" disabled={busy || !amount || Number(amount) <= 0} onClick={() => submit(false)} data-testid="refund-partial">
            Rembourser {amount ? money(Number(amount)) : "…"}
          </Btn>
          <Btn variant="danger" className="w-full" disabled={busy} onClick={() => submit(true)} data-testid="refund-full">
            Remboursement total ({money(booking.price_chf)})
          </Btn>
        </div>
      </div>
    </Drawer>
  );
}

function AssignDrawer({ booking, drivers, vehicles, onClose, onDone }) {
  const [driverId, setDriverId] = useState("");
  const [vehicleId, setVehicleId] = useState(booking.vehicle_id || "");
  const [busy, setBusy] = useState(false);
  const vehicleOf = (d) => vehicles.find((v) => v.usual_driver_id === d.id);
  const submit = async () => {
    if (!driverId || busy) return;
    setBusy(true);
    try {
      const r = await adminApi.post(`/bookings/${booking.id}/assign`, { driver_id: driverId, vehicle_id: vehicleId || null });
      if (r.data.conflict) {
        toast.error("Conflit horaire détecté", { description: r.data.conflicts.map((c) => `${c.tracking_code} à ${c.time}`).join(" · ") });
        return;
      }
      toast.success("Chauffeur attribué — le client est notifié");
      onDone();
    } catch (error) {
      toast.error("Attribution impossible", { description: apiError(error, "Réessayez.") });
    } finally { setBusy(false); }
  };
  const available = drivers.filter((d) => !["suspended", "offline"].includes(d.status));
  return (
    <Drawer title={`Attribuer — ${booking.tracking_code}`} onClose={onClose} testId="assign-drawer">
      <p className="mb-4 text-sm text-slate">{booking.date} · {booking.time} — {booking.pickup} → {booking.dropoff || "à l'heure"}</p>
      <div className="space-y-2">
        {available.length === 0 && <EmptyState title="Aucun chauffeur disponible" hint="Créez un chauffeur dans la section Flotte." />}
        {available.map((d) => {
          const v = vehicleOf(d);
          return (
            <button key={d.id} type="button" onClick={() => setDriverId(d.id)} data-testid={`assign-option-${d.id}`}
              className={`flex w-full items-center gap-3 rounded-xl border p-3.5 text-left transition-all duration-150 ${
                driverId === d.id ? "border-miles bg-miles-ice/60 ring-2 ring-miles/15" : "border-ink/[0.08] bg-white hover:border-miles/30"}`}>
              <Avatar firstName={d.first_name} lastName={d.last_name} size="lg" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-ink">{d.first_name} {d.last_name}</span>
                <span className="block text-xs text-slate">{v ? `${v.make} ${v.model} · ${v.plate}` : "véhicule à définir"} · {d.status === "busy" ? "occupé" : "disponible"}</span>
              </span>
            </button>
          );
        })}
      </div>
      <label className="mt-4 block text-[13px] font-medium text-ink" htmlFor="assign-vehicle">Véhicule (optionnel)</label>
      <select id="assign-vehicle" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} data-testid="assign-vehicle-select" className={`${T.select} mt-1.5`}>
        <option value="">Automatique (véhicule habituel)</option>
        {vehicles.filter((v) => !v.status || v.status === "available" || v.id === vehicleId).map((v) => (
          <option key={v.id} value={v.id}>{v.make} {v.model} · {v.plate}{v.status === "maintenance" ? " (maintenance)" : ""}</option>
        ))}
      </select>
      <Btn className="mt-5 w-full" disabled={!driverId || busy} onClick={submit} data-testid="assign-button">
        {busy ? "Attribution…" : "Attribuer ce chauffeur"}
      </Btn>
    </Drawer>
  );
}

export default function AdminBookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({ date: "", time: "", pickup: "", dropoff: "", price_chf: "" });
  const [note, setNote] = useState("");
  const [incident, setIncident] = useState({ type: "retard", note: "" });
  const [showTech, setShowTech] = useState(false);
  const [busy, setBusy] = useState("");
  const [financeDraft, setFinanceDraft] = useState({});

  const load = useCallback(() => {
    adminApi.get(`/bookings/${id}`).then((r) => {
      setData(r.data);
      const b = r.data.booking;
      setEdit({ date: b.date || "", time: b.time || "", pickup: b.pickup || "", dropoff: b.dropoff || "", price_chf: b.price_chf ?? "" });
    }).catch(() => { toast.error("Réservation introuvable"); navigate("/admin/app/bookings"); });
    adminApi.get("/drivers").then((r) => setDrivers(r.data.drivers)).catch(() => {});
    adminApi.get("/vehicles").then((r) => setVehicles(r.data.vehicles)).catch(() => {});
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Skeleton rows={6} />;
  const b = data.booking;
  const fin = b.internal_finance || {};
  const revenue = b.price_chf || 0;
  const costs = (fin.driver_cost || 0) + (fin.payment_fees || 0) + (fin.other_costs || 0);
  const margin = revenue - costs;
  const marginPct = revenue > 0 ? Math.round((margin / revenue) * 100) : 0;

  const act = async (key, fn, successMsg) => {
    if (busy) return;
    setBusy(key);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      load();
    } catch (error) {
      toast.error("Action impossible", { description: apiError(error, "Réessayez.") });
    } finally { setBusy(""); }
  };

  const copy = (text, label) => navigator.clipboard.writeText(text).then(() => toast.success(`${label} copié`)).catch(() => {});

  return (
    <div className="space-y-5" data-testid="admin-booking-detail">
      <header>
        <Link to="/admin/app/bookings" className="mb-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-slate transition-colors hover:text-miles" data-testid="booking-back">
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} /> Réservations
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-[26px] font-semibold tracking-tight text-ink" data-testid="booking-route">{b.pickup} → {b.dropoff || "à l'heure"}</h1>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate">
              <span className="font-mono text-[13px]" data-testid="booking-code">{b.tracking_code}</span>
              <span>·</span><span className="font-mono text-[13px]">{b.date} · {b.time}</span>
              <span>·</span><StatusBadge status={b.display_status} testId="booking-status" />
              {b.source !== "web" && <span className="rounded-md bg-ink/[0.05] px-1.5 py-0.5 text-[11px] text-slate">{b.source}</span>}
              {data.organization && <span className="rounded-md bg-miles-ice px-1.5 py-0.5 text-[11px] font-medium text-miles">{data.organization.name}</span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Btn variant="secondary" data-testid="copy-tracking-link" onClick={() => copy(`${window.location.origin}/suivi/${b.tracking_code}`, "Lien de suivi")}>
              <Copy className="h-4 w-4" strokeWidth={1.5} /> Lien suivi
            </Btn>
            <a href={`/suivi/${b.tracking_code}`} target="_blank" rel="noreferrer" data-testid="open-tracking"
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-ink/10 bg-white px-4 text-sm font-semibold text-ink transition-all duration-150 hover:border-miles/35 hover:text-miles">
              <ExternalLink className="h-4 w-4" strokeWidth={1.5} /> Tracking
            </a>
            <Btn variant="secondary" data-testid="duplicate-button" disabled={!!busy}
              onClick={() => act("dup", () => adminApi.post(`/bookings/${b.id}/duplicate`).then((r) => navigate(`/admin/app/bookings/${r.data.booking.id}`)), null)}>
              Dupliquer
            </Btn>
            <Btn variant="secondary" data-testid="resend-confirmation" disabled={!!busy}
              onClick={() => act("resend", () => adminApi.post(`/bookings/${b.id}/resend-confirmation`, { origin_url: window.location.origin }), "Confirmation renvoyée au client")}>
              <Mail className="h-4 w-4" strokeWidth={1.5} /> Confirmation
            </Btn>
            <Btn variant="secondary" data-testid="generate-invoice" disabled={!!busy}
              onClick={() => act("invoice", () => adminApi.get(`/invoices/import/${b.id}`)
                .then((r) => adminApi.post("/invoices", r.data))
                .then(() => navigate("/admin/app/invoices")), null)}>
              Facture
            </Btn>
            {!b.cancelled_at && b.display_status !== "completed" && (
              <Btn variant="danger" data-testid="cancel-booking" disabled={!!busy}
                onClick={() => act("cancel", () => adminApi.post(`/bookings/${b.id}/cancel`, { reason: "annulation admin" }), "Réservation annulée")}>
                Annuler
              </Btn>
            )}
            <DeleteButton testId="delete-booking" label="Supprimer"
              onConfirm={() => adminApi.delete(`/bookings/${b.id}`)}
              onDeleted={() => { toast.success("Réservation supprimée"); navigate("/admin/app/bookings"); }} />
          </div>
        </div>
      </header>

      {data.group_units?.length > 0 && (
        <section className="rounded-2xl border border-miles/15 bg-miles-ice/50 p-4" data-testid="booking-group-units">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">
            Réservation groupée · {b.is_group_parent ? "paiement groupé" : `véhicule {b.unit_index}/{b.unit_total}`} · groupe {b.group_id || b.tracking_code}
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {data.group_units.map((u) => (
              <Link key={u.tracking_code} to={`/admin/app/bookings/${u.id}`} data-testid={`group-unit-${u.unit_index}`}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${u.tracking_code === b.tracking_code ? "border-miles bg-white text-ink" : "border-ink/10 bg-white text-slate hover:border-miles/40 hover:text-ink"}`}>
                <span>{u.unit_index}/{u.unit_total}</span>
                <span className="font-mono">{u.tracking_code}</span>
                <StatusBadge status={u.display_status} />
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        {/* ------ colonne principale : trajet d'abord ------ */}
        <div className="space-y-4 lg:col-span-7">
          <Panel testId="booking-journey" className="relative overflow-hidden">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-miles/[0.05] blur-2xl" />
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono text-[34px] font-semibold leading-none tracking-tight text-ink" data-testid="journey-time">{b.time}</p>
                <p className="mt-1.5 text-sm capitalize text-slate">{new Date(`${b.date}T00:00`).toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-[22px] font-semibold text-ink">{money(b.price_chf)}</p>
                <p className="text-xs capitalize text-slate">{b.vehicle_category} · {b.passengers} passager{b.passengers > 1 ? "s" : ""} · {b.luggage} bagage{b.luggage > 1 ? "s" : ""}</p>
              </div>
            </div>
            <div className="mt-5"><RouteLine pickup={b.pickup} dropoff={b.dropoff || `Mise à disposition ${b.duration_hours || 2} h`} testId="journey-route" /></div>
            {(b.stops?.length > 0) && (
              <div className="mt-3 space-y-1.5 rounded-xl border border-ink/[0.06] bg-cloud/40 p-3" data-testid="booking-stops-detail">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate">Arrêts intermédiaires{b.stops_on_return ? " · aussi au retour" : ""}</p>
                {b.stops.map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-[13px]" data-testid={`booking-stop-detail-${i}`}>
                    <span className="min-w-0 flex-1 truncate text-ink">{i + 1}. {s.address}</span>
                    {s.wait_minutes > 0 && <span className="shrink-0 font-mono text-xs text-slate">{s.wait_minutes} min d'attente</span>}
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-slate">
              {b.km ? <span className="font-mono">{b.km} km · {b.duration_min} min</span> : null}
              {b.flight_number && <span className="font-mono" data-testid="journey-flight">Vol {b.flight_number}{b.terminal ? ` · T${b.terminal}` : ""}</span>}
              {b.options?.length > 0 && <span>{b.options.join(", ")}</span>}
            </div>
            {b.notes && <p className="mt-4 rounded-xl bg-amber-50 px-3.5 py-2.5 text-sm text-amber-800" data-testid="journey-notes">{b.notes}</p>}
            <div className="mt-5 border-t border-ink/[0.06] pt-4">
              <button type="button" onClick={() => setEditing(!editing)} className="text-[13px] font-medium text-miles hover:underline" data-testid="edit-journey-toggle">
                {editing ? "Fermer l'édition" : "Modifier / replanifier"}
              </button>
              {editing && (
                <div className="mt-3 grid gap-2.5 sm:grid-cols-2" data-testid="edit-journey-form">
                  <div><label className="text-xs font-medium text-slate">Date</label>
                    <input type="date" value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })} data-testid="edit-date" className={`${T.input} mt-1 h-10 text-sm`} /></div>
                  <div><label className="text-xs font-medium text-slate">Heure</label>
                    <input type="time" value={edit.time} onChange={(e) => setEdit({ ...edit, time: e.target.value })} data-testid="edit-time" className={`${T.input} mt-1 h-10 text-sm`} /></div>
                  <div><label className="text-xs font-medium text-slate">Départ</label>
                    <AddressAutocomplete value={edit.pickup} testId="edit-pickup" inputClass={`${T.input} mt-1 h-10 text-sm`}
                      onChange={(v) => setEdit({ ...edit, pickup: v })} /></div>
                  <div><label className="text-xs font-medium text-slate">Destination</label>
                    <AddressAutocomplete value={edit.dropoff} testId="edit-dropoff" inputClass={`${T.input} mt-1 h-10 text-sm`}
                      onChange={(v) => setEdit({ ...edit, dropoff: v })} /></div>
                  <div><label className="text-xs font-medium text-slate">Prix (CHF)</label>
                    <input type="number" step="0.05" value={edit.price_chf} onChange={(e) => setEdit({ ...edit, price_chf: e.target.value })} data-testid="edit-price" className={`${T.input} mt-1 h-10 font-mono text-sm`} /></div>
                  <div className="flex items-end">
                    <Btn className="w-full" data-testid="save-journey" disabled={!!busy}
                      onClick={() => act("journey", () => adminApi.patch(`/bookings/${b.id}`, {
                        date: edit.date, time: edit.time, pickup: edit.pickup, dropoff: edit.dropoff,
                        price_chf: Number(edit.price_chf),
                      }).then(() => setEditing(false)), "Trajet mis à jour")}>
                      Enregistrer
                    </Btn>
                  </div>
                </div>
              )}
            </div>
          </Panel>

          <Panel title="Progression du trajet" testId="booking-stepper">
            <JourneyStepper current={b.status} disabled={!!b.cancelled_at || !!busy} testId="journey-stepper"
              onSelect={(status) => act(`st-${status}`, () => adminApi.post(`/bookings/${b.id}/status`, { status, origin_url: window.location.origin }), "Statut mis à jour — tracking synchronisé")} />
            <p className="mt-3 text-xs text-slate">Chaque étape met à jour le tracking client et déclenche les notifications associées.</p>
          </Panel>

          <Panel title="Changer le statut" testId="booking-status-control">
            <p className="text-xs font-medium uppercase tracking-wide text-slate">Statut de la course</p>
            <div className="mt-2 grid grid-cols-2 gap-2.5">
              <button type="button" data-testid="status-quick-pending" disabled={!!busy}
                onClick={() => act("qp", () => adminApi.post(`/bookings/${b.id}/status`, { status: "booked", origin_url: window.location.origin }), "Réservation remise en attente")}
                className={`rounded-xl border px-3 py-3 text-sm font-semibold transition-colors ${b.status === "booked" && !b.cancelled_at ? "border-miles bg-miles text-white" : "border-ink/15 bg-white text-ink hover:border-miles/50"}`}>
                En attente
              </button>
              <button type="button" data-testid="status-quick-completed" disabled={!!busy}
                onClick={() => act("qc", () => adminApi.post(`/bookings/${b.id}/status`, { status: "completed", origin_url: window.location.origin }), "Course marquée comme terminée")}
                className={`rounded-xl border px-3 py-3 text-sm font-semibold transition-colors ${b.status === "completed" ? "border-emerald-600 bg-emerald-600 text-white" : "border-ink/15 bg-white text-ink hover:border-emerald-500/50"}`}>
                Terminé
              </button>
            </div>

            <p className="mt-5 text-xs font-medium uppercase tracking-wide text-slate">Statut de paiement</p>
            <select data-testid="payment-status-select" disabled={!!busy}
              value={b.payment_status || "manual"}
              onChange={(e) => act("payst", () => adminApi.post(`/bookings/${b.id}/payment-status`, { payment_status: e.target.value }), "Statut de paiement mis à jour")}
              className="mt-2 h-11 w-full rounded-xl border border-ink/15 bg-white px-3 text-sm text-ink outline-none focus:border-miles">
              <option value="pending">En attente de paiement</option>
              <option value="paid">Payé</option>
              <option value="manual">Réglé hors plateforme</option>
              <option value="due_onboard">À régler à bord</option>
              <option value="invoiced">Sur facture</option>
              <option value="refunded">Remboursé</option>
              <option value="partially_refunded">Partiellement remboursé</option>
              <option value="failed">Échoué</option>
            </select>
            <p className="mt-3 text-xs text-slate">« En attente » réactive une réservation annulée. Le statut de course synchronise le tracking client ; le statut de paiement est interne.</p>
          </Panel>

          <Panel title="Chronologie" testId="booking-timeline">
            {(data.activity || []).length === 0 && <EmptyState title="Aucun événement" hint="L'historique de la réservation apparaîtra ici." />}
            <ol className="relative space-y-0">
              {(data.activity || []).map((e, i, arr) => {
                const h = humanActivity(e);
                return (
                  <li key={e.id} className="relative flex gap-3.5 pb-4">
                    {i < arr.length - 1 && <span className="absolute left-[5px] top-4 h-full w-px bg-ink/[0.08]" />}
                    <span className="relative mt-1.5 h-[11px] w-[11px] shrink-0 rounded-full border-2 border-miles/50 bg-white" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink">{h.title}</p>
                      <p className="text-xs text-slate">
                        {[h.detail, e.tracking_code !== b.tracking_code ? e.tracking_code : null, h.who, e.at?.slice(11, 16)].filter(Boolean).join(" · ")}
                        <span className="text-slate/60"> · {timeAgo(e.at)}</span>
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </Panel>
        </div>

        {/* ------ colonne contexte ------ */}
        <div className="space-y-4 lg:col-span-5">
          <Panel title="Client" testId="booking-customer">
            <div className="flex items-center gap-3">
              <Avatar firstName={b.customer?.first_name} lastName={b.customer?.last_name} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-ink">{b.customer?.first_name} {b.customer?.last_name}</p>
                <p className="truncate text-[13px] text-slate">{b.customer?.email || "pas d'e-mail"}</p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              {b.customer?.phone && (
                <a href={`tel:${b.customer.phone}`} data-testid="call-customer"
                  className="flex h-10 flex-1 items-center justify-center gap-2 rounded-xl border border-ink/10 text-sm font-semibold text-ink transition-colors hover:border-miles/35 hover:text-miles">
                  <Phone className="h-4 w-4" strokeWidth={1.5} /> Appeler
                </a>
              )}
              {b.customer?.email && (
                <a href={`mailto:${b.customer.email}`} data-testid="email-customer"
                  className="flex h-10 flex-1 items-center justify-center gap-2 rounded-xl border border-ink/10 text-sm font-semibold text-ink transition-colors hover:border-miles/35 hover:text-miles">
                  <Mail className="h-4 w-4" strokeWidth={1.5} /> E-mail
                </a>
              )}
            </div>
            {data.customer && (
              <Link to={`/admin/app/customers/${data.customer.id}`} data-testid="open-customer"
                className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-miles hover:underline">
                Ouvrir la fiche client <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
              </Link>
            )}
          </Panel>

          <Panel title="Chauffeur" testId="booking-driver">
            {b.driver ? (
              <div>
                <div className="flex items-center gap-3">
                  <Avatar firstName={b.driver.first_name} lastName={b.driver.last_initial} size="lg" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-semibold text-ink" data-testid="assigned-driver">{b.driver.first_name} {b.driver.last_initial}</p>
                    <p className="text-[13px] text-slate">{[b.driver.vehicle_model, b.driver.plate].filter(Boolean).join(" · ") || "véhicule à confirmer"}</p>
                  </div>
                  <StatusBadge status="driver_assigned" />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {data.driver_record && (
                    <Btn variant="secondary" onClick={() => navigate(`/admin/app/drivers/${data.driver_record.id}`)} data-testid="view-driver">Fiche chauffeur</Btn>
                  )}
                  <Btn variant="secondary" data-testid="assign-replace" onClick={() => setAssignOpen(true)}>Remplacer</Btn>
                  {b.driver_token && (
                    <Btn variant="ghost" data-testid="copy-driver-link"
                      onClick={() => copy(`${window.location.origin}/chauffeur/${b.driver_token}`, "Lien chauffeur")}>
                      <Copy className="h-4 w-4" strokeWidth={1.5} /> Lien chauffeur
                    </Btn>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-xl bg-amber-50/70 p-4" data-testid="no-driver">
                <p className="text-sm font-semibold text-amber-800">Aucun chauffeur attribué</p>
                <p className="mt-0.5 text-[13px] text-amber-700/80">Départ {b.date} à {b.time} — le client attend une confirmation.</p>
                <Btn className="mt-3 w-full" onClick={() => setAssignOpen(true)} data-testid="assign-open">Attribuer un chauffeur</Btn>
              </div>
            )}
          </Panel>

          <Panel title="Paiement" testId="booking-payment"
            action={<StatusBadge status={b.payment_status || "manual"} />}>
            <p className="font-mono text-[24px] font-semibold tracking-tight text-ink">{money(b.price_chf)}</p>
            <p className="mt-0.5 text-xs text-slate">
              {b.payment_status === "paid" ? `Réglé par carte via ${b.payment_provider === "sumup" ? "SumUp" : "Stripe"}` : b.payment_status === "due_onboard" ? (b.payment_provider === "cash" ? "À régler à bord — espèces" : "À régler à bord — carte (terminal SumUp)") : b.payment_status === "partially_refunded" ? "Partiellement remboursé" : b.payment_status === "refunded" ? "Intégralement remboursé" : b.payment_status === "invoiced" ? "Facturation" : b.payment_status === "manual" ? "Réglé hors plateforme" : b.payment_status === "pending" ? "En attente de paiement" : "—"}
            </p>
            {(b.refunds || []).map((r) => (
              <p key={r.id} className="mt-2 flex justify-between rounded-lg bg-red-50 px-3 py-2 text-[13px]">
                <span className="text-red-700">Remboursement du {r.at?.slice(0, 10)}</span>
                <span className="font-mono font-semibold text-red-700">−{money(r.amount_chf)}</span>
              </p>
            ))}
            {b.payment_status === "paid" && (
              <Btn variant="secondary" className="mt-4 w-full" onClick={() => setRefundOpen(true)} data-testid="refund-open">
                Émettre un remboursement
              </Btn>
            )}
            {b.paid_at && <p className="mt-3 text-xs text-slate">Payé le {b.paid_at.slice(0, 16).replace("T", " · ")}</p>}
          </Panel>

          <Panel title="Économie du trajet" testId="booking-finance"
            action={<button type="button" onClick={() => setShowTech(!showTech)} className="text-xs font-medium text-slate hover:text-miles" data-testid="toggle-technical">
              {showTech ? "Masquer" : "Éditer les coûts"}
            </button>}>
            <div className="space-y-1">
              <div className="flex justify-between py-1 text-sm"><span className="text-slate">Revenu</span><span className="font-mono font-medium text-ink">{money(revenue)}</span></div>
              <div className="flex justify-between py-1 text-sm"><span className="text-slate">Coût chauffeur</span><span className="font-mono text-red-600">−{money(fin.driver_cost)}</span></div>
              <div className="flex justify-between py-1 text-sm"><span className="text-slate">Frais de paiement</span><span className="font-mono text-red-600">−{money(fin.payment_fees)}</span></div>
              <div className="flex justify-between py-1 text-sm"><span className="text-slate">Autres coûts</span><span className="font-mono text-red-600">−{money(fin.other_costs)}</span></div>
              <div className="mt-2 flex items-baseline justify-between border-t border-ink/[0.07] pt-3">
                <span className="text-sm font-semibold text-ink">Marge brute</span>
                <span className="text-right">
                  {costs > 0 ? (
                    <>
                      <span className={`font-mono text-lg font-semibold ${margin >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="booking-margin">{money(margin)}</span>
                      <span className="ml-2 font-mono text-xs text-slate">{marginPct} %</span>
                    </>
                  ) : (
                    <span className="font-mono text-lg text-slate" data-testid="booking-margin" title="Renseignez les coûts pour calculer la marge">—</span>
                  )}
                </span>
              </div>
            </div>
            {showTech && (
              <div className="mt-4 space-y-3 border-t border-ink/[0.06] pt-4" data-testid="finance-edit">
                <div className="grid grid-cols-3 gap-2">
                  {[["Coût chauffeur", "driver_cost"], ["Frais paie.", "payment_fees"], ["Autres", "other_costs"]].map(([label, key]) => (
                    <div key={key}>
                      <label className="text-[11px] font-medium text-slate" htmlFor={`fin-${key}`}>{label}</label>
                      <input id={`fin-${key}`} type="number" step="0.05" defaultValue={fin[key] ?? 0}
                        data-testid={`fin-${key}`} className={`${T.input} mt-1 h-9 font-mono text-[13px]`}
                        onChange={(e) => setFinanceDraft((f) => ({ ...f, [key]: e.target.value }))} />
                    </div>
                  ))}
                </div>
                <Btn variant="secondary" className="w-full" disabled={!!busy} data-testid="save-finance"
                  onClick={() => act("fin", () => adminApi.patch(`/bookings/${b.id}`, {
                    driver_cost: Number(financeDraft.driver_cost ?? fin.driver_cost ?? 0),
                    payment_fees: Number(financeDraft.payment_fees ?? fin.payment_fees ?? 0),
                    other_costs: Number(financeDraft.other_costs ?? fin.other_costs ?? 0),
                  }), "Coûts enregistrés")}>
                  Enregistrer les coûts
                </Btn>
                {(b.stripe_payment_intent_id || b.sumup_checkout_id || data.payment_transaction) && (
                  <div className="rounded-xl bg-ink/[0.03] p-3" data-testid="technical-details">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate/60">Détails techniques</p>
                    {b.stripe_payment_intent_id && <p className="mt-1.5 break-all font-mono text-[11px] text-slate">PI · {b.stripe_payment_intent_id}</p>}
                    {b.sumup_checkout_id && <p className="mt-1.5 break-all font-mono text-[11px] text-slate">SumUp · {b.sumup_checkout_id}</p>}
                    <p className="mt-1 break-all font-mono text-[11px] text-slate">ID · {b.id}</p>
                  </div>
                )}
              </div>
            )}
            {!showTech && (b.stripe_payment_intent_id || b.id) && null}
          </Panel>

          <Panel title="Notes internes" testId="booking-notes">
            <div className="space-y-2.5">
              {(b.internal_notes || []).map((n, i) => (
                <div key={i} className="flex gap-2.5">
                  <Avatar firstName={n.by?.split("@")[0]} size="sm" />
                  <div className="min-w-0">
                    <p className="text-sm text-ink">{n.text}</p>
                    <p className={T.meta}>{n.by?.split("@")[0]} · {n.at?.slice(5, 16).replace("T", " ")}</p>
                  </div>
                </div>
              ))}
              {(b.internal_notes || []).length === 0 && <p className="text-[13px] text-slate">Aucune note — invisible pour le client.</p>}
            </div>
            <div className="mt-3 flex gap-2 border-t border-ink/[0.06] pt-3">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ajouter une note interne…"
                data-testid="internal-note-input" className={`${T.input} h-10 text-sm`} />
              <Btn variant="secondary" className="h-10 px-3" data-testid="add-note" disabled={!note.trim() || !!busy}
                onClick={() => act("note", () => adminApi.patch(`/bookings/${b.id}`, { internal_note: note }).then(() => setNote("")), "Note ajoutée")}>
                <Plus className="h-4 w-4" strokeWidth={2} />
              </Btn>
            </div>
          </Panel>

          <Panel title={`Incidents (${(b.incidents || []).length})`} testId="booking-incidents">
            <div className="space-y-2">
              {(b.incidents || []).map((inc) => (
                <div key={inc.id} className={`flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm ${inc.status === "open" ? "bg-amber-50" : "bg-ink/[0.03]"}`}>
                  <div>
                    <p className="font-medium capitalize text-ink">{inc.type}</p>
                    {inc.note && <p className="text-xs text-slate">{inc.note}</p>}
                    <p className={T.meta}>{inc.by?.split("@")[0]} · {inc.at?.slice(5, 16).replace("T", " ")}</p>
                  </div>
                  {inc.status === "open" ? (
                    <Btn variant="ghost" className="h-8 text-xs text-miles" data-testid={`resolve-${inc.id}`}
                      onClick={() => act(`res-${inc.id}`, () => adminApi.post(`/bookings/${b.id}/incidents/${inc.id}/resolve`), "Incident résolu")}>
                      Résoudre
                    </Btn>
                  ) : <StatusBadge status="completed" />}
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2 border-t border-ink/[0.06] pt-3">
              <select value={incident.type} onChange={(e) => setIncident({ ...incident, type: e.target.value })}
                data-testid="incident-type" className={`${T.select} h-10 w-36 text-sm`}>
                {INCIDENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input value={incident.note} onChange={(e) => setIncident({ ...incident, note: e.target.value })}
                placeholder="Détail…" data-testid="incident-note" className={`${T.input} h-10 text-sm`} />
              <Btn variant="secondary" className="h-10 px-3" data-testid="add-incident" disabled={!!busy}
                onClick={() => act("inc", () => adminApi.post(`/bookings/${b.id}/incidents`, incident).then(() => setIncident({ type: "retard", note: "" })), "Incident enregistré")}>
                <Plus className="h-4 w-4" strokeWidth={2} />
              </Btn>
            </div>
          </Panel>

          <Panel title="Communications" testId="booking-comms">
            {(data.communications || []).length === 0 && <p className="text-[13px] text-slate">Aucun envoi pour cette réservation.</p>}
            <div className="space-y-2.5">
              {(data.communications || []).map((c) => (
                <div key={c.id} className="flex items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-miles-ice">
                    <Mail className="h-3.5 w-3.5 text-miles" strokeWidth={1.5} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink">{COMM_LABELS[c.template] || c.template}</p>
                    <p className="truncate text-xs text-slate">E-mail envoyé à {c.to}</p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                    {c.status === "sent" ? "Distribué" : "Échoué"}
                  </span>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>

      {assignOpen && <AssignDrawer booking={b} drivers={drivers} vehicles={vehicles} onClose={() => setAssignOpen(false)} onDone={() => { setAssignOpen(false); load(); }} />}
      {refundOpen && <RefundDrawer booking={b} onClose={() => setRefundOpen(false)} onDone={() => { setRefundOpen(false); load(); }} />}
    </div>
  );
}
